import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Fireworks } from 'fireworks-js'

const DEFAULT_FORM_DATA = {
  classType: '',
  capacity: '',
  reminderTiming: '',
  bookingWindow: ''
}

const buildRiskConfig = (content) => ({
  low: {
    label: content?.results?.low_label,
    colorClass: 'no-show-scanner-risk--low',
    iconClass: 'fa-solid fa-circle-check'
  },
  medium: {
    label: content?.results?.medium_label,
    colorClass: 'no-show-scanner-risk--medium',
    iconClass: 'fa-solid fa-triangle-exclamation'
  },
  high: {
    label: content?.results?.high_label,
    colorClass: 'no-show-scanner-risk--high',
    iconClass: 'fa-solid fa-circle-exclamation'
  }
})

const capitalize = (value) => {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const buildFactorsLabel = (items, joiner) => {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  return `${items[0]} ${joiner} ${items[1]}`
}

const calculateRisk = (data, content) => {
  let riskScore = 0
  const factors = []
  const suggestions = []
  const suggestionCopy = content?.suggestions || {}
  const factorCopy = content?.factors || {}

  if (data.reminderTiming === 'none') {
    riskScore += 3
    factors.push(factorCopy.no_reminders)
    suggestions.push(suggestionCopy.reminders_both)
  } else if (data.reminderTiming === 'day-before') {
    riskScore += 1
    suggestions.push(suggestionCopy.add_same_day)
  } else if (data.reminderTiming === 'day-of') {
    riskScore += 1
    suggestions.push(suggestionCopy.add_day_before)
  }

  if (data.bookingWindow === 'same-day') {
    riskScore += 2
    factors.push(factorCopy.same_day_booking)
    suggestions.push(suggestionCopy.require_24)
  } else if (data.bookingWindow === 'anytime') {
    riskScore += 1
    suggestions.push(suggestionCopy.consider_cutoff)
  }

  const capacity = parseInt(data.capacity, 10) || 0
  if (capacity > 15) {
    riskScore += 1
    factors.push(factorCopy.large_class)
    suggestions.push(suggestionCopy.enable_waitlist)
  }

  if (capacity > 0 && capacity <= 6) {
    suggestions.push(suggestionCopy.small_class)
  }

  if (data.classType === 'Hot Yoga' || data.classType === 'HIIT') {
    riskScore += 1
    factors.push(factorCopy.high_intensity)
  }

  let level = 'low'
  let explanation = content?.explanations?.low || ''
  const joiner = content?.text?.and_joiner || 'and'

  if (riskScore <= 2) {
    level = 'low'
    explanation = content?.explanations?.low || ''
  } else if (riskScore <= 4) {
    level = 'medium'
    const factorText = buildFactorsLabel(factors.filter(Boolean), joiner) || content?.text?.default_factors || ''
    explanation = (content?.explanations?.medium || '').replace('%{factors}', capitalize(factorText))
  } else {
    level = 'high'
    const combinedJoiner = content?.text?.combined_with || 'combined with'
    const factorText = buildFactorsLabel(factors.filter(Boolean), combinedJoiner) || content?.text?.default_factors || ''
    explanation = (content?.explanations?.high || '').replace('%{factors}', capitalize(factorText))
  }

  return {
    level,
    explanation,
    suggestions: suggestions.filter(Boolean).slice(0, 3)
  }
}

const cardClass = 'no-show-scanner-card mb-6 overflow-hidden bg-marketing-paper'
const cardBodyClass = 'px-4 py-5 sm:px-6 lg:px-8'
const headingClass = 'mb-5 flex items-center gap-3 text-lg font-semibold text-marketing-forest sm:text-xl'
const optionClass = 'no-show-scanner-option inline-flex items-center justify-center text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2'

const NoShowScanner = ({ content = {} }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA)
  const [result, setResult] = useState(null)
  const [showResult, setShowResult] = useState(false)
  const fireworksRef = useRef(null)
  const fireworksInstance = useRef(null)

  const options = content.options || {}
  const classTypes = options.class_types || []
  const reminderOptions = options.reminder_timing || []
  const bookingWindows = options.booking_windows || []

  const riskConfig = useMemo(() => buildRiskConfig(content), [content])

  const isFormComplete = Boolean(
    formData.classType &&
      formData.capacity &&
      formData.reminderTiming &&
      formData.bookingWindow
  )

  useEffect(() => {
    if (!showResult || result?.level !== 'low') {
      if (fireworksInstance.current) {
        fireworksInstance.current.stop()
        fireworksInstance.current = null
      }
      return undefined
    }

    if (!fireworksRef.current) return undefined

    const instance = new Fireworks(fireworksRef.current, {
      hue: { min: 120, max: 150 },
      rocketsPoint: { min: 20, max: 80 },
      speed: 3,
      acceleration: 1.05,
      friction: 0.95,
      gravity: 1.3,
      particles: 60,
      traceLength: 2,
      traceSpeed: 6,
      explosion: 4
    })

    instance.start()
    fireworksInstance.current = instance

    const timer = setTimeout(() => {
      instance.stop()
    }, 4500)

    return () => {
      clearTimeout(timer)
      instance.stop()
      fireworksInstance.current = null
    }
  }, [showResult, result])

  useEffect(() => {
    if (!showResult) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [showResult])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!isFormComplete) return
    const riskResult = calculateRisk(formData, content)
    setResult(riskResult)
    setShowResult(true)
  }

  const handleReset = () => {
    setShowResult(false)
    setResult(null)
    setFormData(DEFAULT_FORM_DATA)
  }

  const handleOptionSelect = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const riskDisplay = result ? riskConfig[result.level] : null

  return (
    <section className="no-show-scanner-page pb-16 pt-8 lg:pb-24 lg:pt-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {!showResult ? (
          <form onSubmit={handleSubmit} className="no-show-scanner-form mx-auto">
            <div className={cardClass}>
              <div className={cardBodyClass}>
                <label className={headingClass}>
                  <i className="fa-solid fa-calendar text-primary" aria-hidden="true" />
                  {content?.form?.class_type_label}
                </label>
                <div className="flex flex-wrap gap-2">
                  {classTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleOptionSelect('classType', type)}
                      className={`${optionClass} ${formData.classType === type ? 'no-show-scanner-option--active' : ''}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <label className={headingClass}>
                  <i className="fa-solid fa-users text-primary" aria-hidden="true" />
                  {content?.form?.capacity_label}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.capacity}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      capacity: event.target.value
                    }))
                  }
                  placeholder={content?.form?.capacity_placeholder}
                  className="no-show-scanner-input w-full rounded-2xl bg-marketing-paper px-4 py-3 text-base text-marketing-forest outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <label className={headingClass}>
                  <i className="fa-solid fa-bell text-primary" aria-hidden="true" />
                  {content?.form?.reminder_label}
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {reminderOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionSelect('reminderTiming', option.value)}
                      className={`${optionClass} no-show-scanner-option--block ${formData.reminderTiming === option.value ? 'no-show-scanner-option--active' : ''}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <label className={headingClass}>
                  <i className="fa-solid fa-clock text-primary" aria-hidden="true" />
                  {content?.form?.booking_window_label}
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {bookingWindows.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionSelect('bookingWindow', option.value)}
                      className={`${optionClass} no-show-scanner-option--block ${formData.bookingWindow === option.value ? 'no-show-scanner-option--active' : ''}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="no-show-scanner-submit inline-flex w-full items-center justify-center gap-2 text-lg disabled:opacity-60"
              disabled={!isFormComplete}
            >
              {content?.form?.submit_label}
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </form>
        ) : (
          <div className="no-show-scanner-results mx-auto">
            {result && riskDisplay && (
              <div className={`${cardClass} no-show-scanner-risk ${riskDisplay.colorClass}`}>
                {result.level === 'low' && (
                  <div className="no-show-scanner-fireworks" ref={fireworksRef} aria-hidden="true" />
                )}
                <div className="no-show-scanner-risk__body px-6 py-8 sm:px-8">
                  <div className="flex justify-center">
                    <div className="no-show-scanner-risk__icon">
                      <i className={riskDisplay.iconClass} aria-hidden="true" />
                    </div>
                  </div>
                  <h2 className="mb-3 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
                    {riskDisplay.label}
                  </h2>
                  <p className="text-center text-base leading-7 text-marketing-forest sm:text-lg">
                    {result.explanation}
                  </p>
                </div>
              </div>
            )}

            {result && (
              <div className={cardClass}>
                <div className="px-6 py-8 sm:px-8">
                  <h3 className="mb-4 font-display text-3xl font-normal tracking-tight text-marketing-forest">
                    {content?.results?.suggestions_title}
                  </h3>
                  <ul className="no-show-scanner-suggestions m-0 list-none p-0">
                    {result.suggestions.map((suggestion) => (
                      <li className="flex items-start gap-3 text-base leading-7 text-marketing-muted sm:text-lg" key={suggestion}>
                        <i className="fa-solid fa-circle-check mt-1 text-primary" aria-hidden="true" />
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className={`${cardClass} no-show-scanner-cta`}>
              <div className="px-6 py-8 text-center sm:px-8">
                <p className="mb-4 text-lg leading-8 text-marketing-forest sm:text-2xl">
                  <strong>{content?.results?.cta_strong}</strong> {content?.results?.cta_body}
                </p>
                <a
                  className="no-show-scanner-submit inline-flex items-center gap-2"
                  href={content?.results?.cta_link}
                >
                  <span className="text-left">
                    {content?.results?.cta_link_label_prefix}
                    <br />
                    {content?.results?.cta_link_label_emphasis}
                  </span>
                  <i className="fa-solid fa-arrow-right text-white" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleReset}
                className="no-show-scanner-reset inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:bg-white"
              >
                {content?.form?.reset_label}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default NoShowScanner
